/**
 * useDealLiveEvents(dealId) — consumes the loan's deal-group collaboration events
 * (fanned out by RealtimeProvider.subscribeDeal) for the open loan:
 *
 *   • PRESENCE — who else has this loan open right now. Tracks `presence_join` /
 *     `presence_leave` / `presence_sync` events into a viewer list (self excluded),
 *     and surfaces the most recent joiner so the UI can toast "X opened this loan".
 *
 *   • UPDATE — `deal_updated` (the backend already broadcasts this when the loan
 *     changes). Surfaced as a NON-destructive pending prompt — never auto-refetch,
 *     the viewer may have unsaved edits. Own changes (`changedBy === self`) ignored.
 *
 * Presence depends on the backend broadcasting `presence_*` to `deal:{id}` (and on
 * Web PubSub being provisioned); until then the viewer list just stays empty. The
 * `deal_updated` half works as soon as Web PubSub is live — no backend change.
 *
 * Event shapes consumed (all carry { v, type, dealId, at }):
 *   presence_sync  { viewers: [{ email, name }] }          // full set, sent to a joiner
 *   presence_join  { user: { email, name }, connectionId } // someone opened the loan
 *   presence_leave { user: { email },        connectionId } // someone closed it
 *   deal_updated   { changedBy, impact, change:{topic,kind,subject} }
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { useRealtime } from "./RealtimeProvider";

const normEmail = (e) => (e || "").trim().toLowerCase();

export function useDealLiveEvents(dealId) {
  const { subscribeDeal, status } = useRealtime();
  const { user } = useAuth();
  const selfEmail = normEmail(user?.email);

  // viewers: email → { email, name }. Self is never added.
  const [viewers, setViewers] = useState([]);
  const viewerMapRef = useRef(new Map());

  // Most recent non-self joiner (for a one-shot toast). Carries a nonce so the
  // same person re-joining still fires.
  const [lastJoin, setLastJoin] = useState(null);

  // Pending update prompt from someone else's change.
  const [update, setUpdate] = useState(null);

  const flushViewers = useCallback(() => {
    setViewers(Array.from(viewerMapRef.current.values()));
  }, []);

  useEffect(() => {
    if (!dealId) return undefined;
    // Reset per-loan state.
    viewerMapRef.current = new Map();
    setViewers([]);
    setLastJoin(null);
    setUpdate(null);

    const handle = (ev) => {
      const t = ev?.type;
      if (t === "presence_sync") {
        const map = new Map();
        (ev.viewers || []).forEach((v) => {
          const em = normEmail(v.email);
          if (em && em !== selfEmail) map.set(em, { email: em, name: v.name || em });
        });
        viewerMapRef.current = map;
        flushViewers();
      } else if (t === "presence_join") {
        const em = normEmail(ev.user?.email);
        if (em && em !== selfEmail) {
          const name = ev.user?.name || em;
          viewerMapRef.current.set(em, { email: em, name });
          flushViewers();
          setLastJoin({ email: em, name, at: ev.at });
        }
      } else if (t === "presence_leave") {
        const em = normEmail(ev.user?.email);
        if (em && viewerMapRef.current.delete(em)) flushViewers();
      } else if (t === "deal_updated") {
        if (normEmail(ev.changedBy) === selfEmail) return; // ignore own changes
        setUpdate((prev) => ({
          at: ev.at,
          impact: ev.impact || "normal",
          change: ev.change || null,
          changedBy: normEmail(ev.changedBy),
          count: (prev?.count || 0) + 1,
        }));
      }
    };

    const unsubscribe = subscribeDeal(dealId, handle);
    return () => unsubscribe();
  }, [dealId, selfEmail, subscribeDeal, flushViewers]);

  const dismissUpdate = useCallback(() => setUpdate(null), []);
  const clearLastJoin = useCallback(() => setLastJoin(null), []);

  return useMemo(
    () => ({
      viewers,
      lastJoin,
      clearLastJoin,
      update,
      dismissUpdate,
      realtimeLive: status === "connected",
    }),
    [viewers, lastJoin, clearLastJoin, update, dismissUpdate, status],
  );
}

export default useDealLiveEvents;
